import type { RootState } from '@/rootReducer';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchHubspotCompany } from '@/modules/company/companyActions';

interface Company {
	id: string;
	companyHsId: string;
	name: string;
}

interface Hscompany {
	companyName: string;
	companyHsId: string;
}

interface SelectCompanyProps {
	allCompanies: Company[];
	selectHubspotCompany: (company: Hscompany | null) => void;
}

const HubspotCompanySelector: React.FC<SelectCompanyProps> = ({
	allCompanies,
	selectHubspotCompany
}) => {
	const dispatch = useDispatch();

	const [searchInput, setSearchInput] = useState<string>('');
	const [filteredCompanies, setFilteredCompanies] = useState<Hscompany[]>([]);
	const [companySelected, setCompanySelected] = useState<Hscompany | null>(
		null
	);
	const [showDropDown, setShowDropDown] = useState<boolean>(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLUListElement>(null);
	const loading = useSelector((state: RootState) =>
		state.company.getIn(['searchCompanies', 'loading'])
	);
	const companiesList = useSelector((state: RootState) =>
		state.company.getIn(['searchCompanies', 'data'])
	);

	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const handleSearchChange = (value: string) => {
		if (value !== companySelected?.companyName) {
			setCompanySelected(null);
			selectHubspotCompany(null);
		}

		setSearchInput(value);
		setShowDropDown(true);

		if (!value) {
			setShowDropDown(false);
			setFilteredCompanies([]);
			return;
		}

		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		debounceTimeoutRef.current = setTimeout(() => {
			dispatch(searchHubspotCompany(value));
		}, 500);
	};

	useEffect(() => {
		if (companiesList.size > 0) {
			const onlyNonexistant = companiesList.filter(
				(company: Hscompany) => {
					return (
						allCompanies.find(
							(c: Company) =>
								c.companyHsId === company.companyHsId
						) === undefined
					);
				}
			);

			setFilteredCompanies(onlyNonexistant);
		} else {
			setFilteredCompanies([]);
		}
	}, [companiesList]);

	const handleSelectCompany = (company: Hscompany) => {
		setShowDropDown(false);
		setSearchInput(company.companyName);
		setCompanySelected(company);
		selectHubspotCompany(company);
	};

	useEffect(() => {
		if (companiesList.length > 0) {
			const onlyNonexistant = companiesList.filter(
				(company: Hscompany) => {
					return (
						allCompanies.find(
							(c: Company) =>
								c.companyHsId === company.companyHsId
						) === undefined
					);
				}
			);

			setFilteredCompanies(onlyNonexistant);
		}
	}, [companiesList]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!(
					dropdownRef.current.contains(event.target as Node) ||
					inputRef.current?.contains(event.target as Node)
				)
			) {
				setShowDropDown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<div>
			<input
				onFocus={() => setShowDropDown(true)}
				ref={inputRef}
				type="text"
				id="hubspot-company-input"
				autoComplete="off"
				value={searchInput}
				onChange={e => handleSearchChange(e.target.value)}
				placeholder="Search Hubspot Company"
			/>
			{!loading && showDropDown && filteredCompanies.length > 0 && (
				<ul className="acc-create-dropdown-list" ref={dropdownRef}>
					{filteredCompanies.map((company: Hscompany) => (
						<li
							key={company.companyHsId}
							onClick={() => handleSelectCompany(company)}
						>
							{`(${company.companyHsId}) - ${company.companyName}`}
						</li>
					))}
				</ul>
			)}
			{loading && <div className="loading-indicator">Loading...</div>}{' '}
		</div>
	);
};

export default HubspotCompanySelector;
